import {
  useUserProfileFind,
  useUserProfileUpdateEmail,
  useUserProfileresetPassword,
} from "@/lib/api/requests/user-profile.requests";
import { useApiClient } from "@/lib/api/client";
import { Context } from "@/util-components/root/context";
import { UserInfoUserType } from "@/lib/api/interfaces/user-profile.interface";
import { removeAllPageStates } from "@/lib/utils/page-state";
import { clearGlobalInfoBannerDismissStates } from "@/util-components/root/global-info-banner";

import { Button } from "@/ui-components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { SuspenseWrapper } from "@/ui-components/ui/suspense-wrapper";
import { Card, CardContent, CardHeader } from "@/ui-components/ui/card";
import { SectionHeader } from "@/util-components/page-layout/section-header";
import { CardDashboardItem, CardDashboardPage } from "@/util-components/page-layout/card-dashboard-page";
import { SubPageLayout } from "@/util-components/page-layout/sub-page-layout";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "@/lib/toast";
import { useEffect, useState, useContext } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { KeyRound, Loader2, Settings as SettingsIcon, ShieldCheck, UserRound, Mail, RefreshCw, PlayCircle } from "lucide-react";

const emailUpdateSchema = z.object({
  mail: z.string().email("Bitte gib eine gültige Email Adresse ein"),
});

type EmailUpdateForm = z.infer<typeof emailUpdateSchema>;
  
  export const SettingsDashboard = () => {
  const { user } = useContext(Context);
  
  const items: CardDashboardItem[] = [
    {
      title: "Profil",
      description: "Deine persönlichen Daten und Email-Adresse.",
      icon: UserRound,
      path: "/settings/profil",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-100",
    },
    {
      title: "Sicherheit",
      description: "Passwort zurücksetzen und Sicherheitsoptionen.",
      icon: ShieldCheck,
      path: "/settings/sicherheit",
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
      borderColor: "border-indigo-100",
    },
    {
      title: "App-Einstellungen",
      description: "Lokale Einstellungen und Zustände zurücksetzen.",
      icon: RefreshCw,
      path: "/settings/app",
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-100",
    },
  ];

  if (user?.userType === UserInfoUserType.CUSTOMER_USER) {
    items.push({
      title: "Onboarding-Tour",
      description: "Starte die interaktive Tour durch den Post-Generator erneut.",
      icon: PlayCircle,
      path: `/?simulateOnboarding=tour&simulateOnboardingPlan=${user.subscriptionType || 'basic'}`,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-100",
    });
  }

  return (
    <CardDashboardPage
      basePath="/settings"
      pageTitle="Einstellungen"
      pageIcon={SettingsIcon}
      pageIconColor="text-blue-600"
      pageIconBg="bg-blue-50"
      sectionTitle="Konto"
      sectionDescription="Verwalte deine Kontoeinstellungen an einem Ort."
      items={items}
    />
  );
};

const SettingsProfile = () => {
  const { data: userProfile } = useUserProfileFind();
  const updateEmailMutation = useUserProfileUpdateEmail();
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);

  const emailForm = useForm<EmailUpdateForm>({
    resolver: zodResolver(emailUpdateSchema),
    defaultValues: {
      mail: "",
    },
  });

  // Update form when userProfile data is loaded
  useEffect(() => {
    if (userProfile?.mail) {
      emailForm.reset({
        mail: userProfile.mail,
      });
    }
  }, [userProfile?.mail, emailForm]);

  const onEmailUpdate = async (data: EmailUpdateForm) => {
    try {
      setIsUpdatingEmail(true);
      await updateEmailMutation.mutateAsync(data);
      toast.success("Email erfolgreich aktualisiert");
    } catch (error) {
      toast.error("Email konnte nicht aktualisiert werden", { error });
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="rounded-xl shadow-sm border-gray-200">
        <CardHeader>
          <SectionHeader
            icon={UserRound}
            title="Profil"
            description="Deine persönlichen Informationen."
          />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900">Vorname</p>
              <Input value={userProfile?.firstName || ""} disabled className="bg-gray-50" />
              <p className="text-xs text-gray-500">Kontaktiere deinen Administrator, um deinen Namen zu ändern.</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900">Nachname</p>
              <Input value={userProfile?.lastName || ""} disabled className="bg-gray-50" />
              <p className="text-xs text-gray-500">Kontaktiere deinen Administrator, um deinen Namen zu ändern.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl shadow-sm border-gray-200">
        <CardHeader>
          <SectionHeader
            icon={Mail}
            title="Email-Adresse"
            description="Aktualisiere deine Login-Email."
          />
        </CardHeader>
        <CardContent>
          <Form {...emailForm}>
            <form onSubmit={emailForm.handleSubmit(onEmailUpdate)} className="space-y-4">
              <FormField
                control={emailForm.control}
                name="mail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isOptional={false}>Email</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" placeholder="Gib deine Email-Adresse ein" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={isUpdatingEmail || !emailForm.formState.isDirty}
                  className="h-11 bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all rounded-xl"
                >
                  {isUpdatingEmail ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Wird gespeichert...
                    </>
                  ) : (
                    "Email aktualisieren"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export const SettingsProfilePage = () => {
  const navigate = useNavigate();

  return (
    <SubPageLayout
      pageTitle="Profil"
      parentPageTitle="Einstellungen"
      parentPageCallback={() => navigate("/settings")}
      navigateBackCallback={() => navigate("/settings")}
      icon={<UserRound className="h-6 w-6" />}
    >
      <div className="max-w-4xl mx-auto py-8 px-4 md:px-6">
        <SuspenseWrapper type="green" text="Lade dein Profil...">
          <SettingsProfile />
        </SuspenseWrapper>
      </div>
    </SubPageLayout>
  );
};

const SettingsSecurity = () => {
  const { apiRequest } = useApiClient();
  const resetPasswordMutation = useUserProfileresetPassword();
  const [shouldCrash, setShouldCrash] = useState(false);

  if (shouldCrash) {
    throw new Error("Test Error for ErrorBoundary");
  }

  const onPasswordReset = async () => {
    try {
      await resetPasswordMutation.mutateAsync();
      toast.success("Passwort-Reset-Email erfolgreich gesendet");
    } catch (error) {
      toast.error("Passwort-Reset fehlgeschlagen", { error });
    }
  };

  const handleManualToastOverrideTest = () => {
    try {
      throw new Error("Manuell ausgelöster Testfehler für den neuen Details-Toast.");
    } catch (error) {
      toast.error("Toast.error Override Test", {
        description: "Dieser Fehler wurde lokal geworfen, gefangen und mit Details-Button angezeigt.",
        error,
      });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="rounded-xl shadow-sm border-gray-200">
        <CardHeader>
          <SectionHeader
            icon={KeyRound}
            title="Passwort"
            description="Sende dir einen sicheren Reset-Link."
          />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Um dein Passwort zu ändern, senden wir dir einen sicheren Reset-Link an deine Email-Adresse.
            </p>
            <div className="pt-2">
              <Button
                onClick={onPasswordReset}
                disabled={resetPasswordMutation.isPending}
                className="h-11 bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all rounded-xl"
              >
                {resetPasswordMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  "Passwort-Reset-Email senden"
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {process.env.NODE_ENV === "development" && (
        <Card className="rounded-xl shadow-sm border-red-200">
          <CardHeader>
            <SectionHeader
              icon={ShieldCheck}
              title="Entwickler-Tools"
              description="Nur in Development sichtbar."
            />
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl"
                onClick={() => setShouldCrash(true)}
              >
                Fehler provozieren (ErrorBoundary Test)
              </Button>
              <Button
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl"
                onClick={async () => {
                  try {
                    await apiRequest("/test-error-endpoint-for-modal-check", "GET");
                  } catch (e) {
                    console.log("Test error triggered:", e);
                  }
                }}
              >
                HTTP Fehler provozieren (API Toast Test)
              </Button>
              <Button
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl"
                onClick={handleManualToastOverrideTest}
              >
                Toast.error Override Test
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export const SettingsSecurityPage = () => {
  const navigate = useNavigate();

  return (
    <SubPageLayout
      pageTitle="Sicherheit"
      parentPageTitle="Einstellungen"
      parentPageCallback={() => navigate("/settings")}
      navigateBackCallback={() => navigate("/settings")}
      icon={<ShieldCheck className="h-6 w-6" />}
    >
      <div className="max-w-4xl mx-auto py-8 px-4 md:px-6">
        <SettingsSecurity />
      </div>
    </SubPageLayout>
  );
};

const SettingsApp = () => {
  const [isResetting, setIsResetting] = useState(false);

  const handleReset = () => {
    setIsResetting(true);
    try {
      const count = removeAllPageStates() + clearGlobalInfoBannerDismissStates();

      if (count > 0) {
        toast.success(`${count} Einstellungen wurden zurückgesetzt.`);
      } else {
        toast.success("Keine lokalen Einstellungen gefunden.");
      }
      
      // Kurze Verzögerung, damit der User das Feedback sieht
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      toast.error("Zurücksetzen fehlgeschlagen", { error });
      setIsResetting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="rounded-xl shadow-sm border-gray-200">
        <CardHeader>
          <SectionHeader
            icon={RefreshCw}
            title="Lokale Einstellungen zurücksetzen"
            description="Setzt alle gespeicherten Ansichten und Filter zurück."
          />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-800 leading-relaxed">
                <strong>Was passiert hier?</strong><br />
                SportyPost merkt sich, wie du die App nutzt (z.B. Listen- vs. Rasteransicht, ob Info-Banner ausgeblendet wurden, Filter-Einstellungen etc.).
                Diese Daten werden lokal auf deinem Gerät gespeichert.
              </p>
              <p className="text-sm text-gray-800 leading-relaxed mt-2">
                Wenn du auf "Zurücksetzen" klickst, werden diese Informationen gelöscht und die App verhält sich wieder so, als würdest du sie zum ersten Mal öffnen.
                Deine Account-Daten, Posts oder Templates bleiben dabei <strong>vollständig erhalten</strong>.
              </p>
            </div>
            
            <div className="pt-2">
              <Button
                onClick={handleReset}
                disabled={isResetting}
                className="h-11 bg-orange-600 hover:bg-orange-700 text-white shadow-sm transition-all rounded-xl w-full sm:w-auto"
              >
                {isResetting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Wird zurückgesetzt...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Einstellungen zurücksetzen & neu laden
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export const SettingsAppPage = () => {
  const navigate = useNavigate();

  return (
    <SubPageLayout
      pageTitle="App-Einstellungen"
      parentPageTitle="Einstellungen"
      parentPageCallback={() => navigate("/settings")}
      navigateBackCallback={() => navigate("/settings")}
      icon={<RefreshCw className="h-6 w-6" />}
    >
      <div className="max-w-4xl mx-auto py-8 px-4 md:px-6">
        <SettingsApp />
      </div>
    </SubPageLayout>
  );
};

export default SettingsDashboard;
